import { CostPriceAnalysis } from '../services/costPrice/costPriceService';
import { eur } from '../components/orderhistory/utils';

export interface GroupedIngredients {
  [key: string]: CostPriceAnalysis['ingredients'];
}

export interface SubrecipeInfo {
  name: string;
  ingredientCount: number;
  portionsNeeded: number;
  portionsUnit: string;
  totalCost: number;
}

/**
 * Groups ingredients into: 'null' (standalone), 'nested-{id}' (nested cost price items).
 * Subrecipe reference rows are intentionally excluded here — they're handled by the
 * caller's live store join (see useCostPriceDetail.subrecipesWithTotals).
 */
export const groupIngredientsBySubrecipe = (
  analysis: CostPriceAnalysis | null
): GroupedIngredients => {
  if (!analysis) return {};

  return analysis.ingredients.reduce((groups, ingredient) => {
    // Subrecipe reference row — excluded; handled separately.
    if ((ingredient as any).subrecipeId && !(ingredient as any).articleId) {
      return groups;
    }
    const nestedCostPriceId = (ingredient as any).nestedCostPriceId;
    if (nestedCostPriceId) {
      const groupKey = `nested-${nestedCostPriceId}`;
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(ingredient);
    } else {
      if (!groups['null']) groups['null'] = [];
      groups['null'].push(ingredient);
    }
    return groups;
  }, {} as GroupedIngredients);
};

/**
 * Gets the display unit for portions
 */
export const getPortionsUnitDisplay = (unit: string): string => {
  switch (unit) {
    case 'g':
      return 'g';
    case 'ml':
      return 'ml';
    default:
      return 'portions';
  }
};

// Scales g→kg and ml→L so subrecipe unit prices match the base-unit format
// used for regular/custom ingredients (e.g. €12.50/kg rather than €0.0125/g).
export const formatSubrecipeUnitPrice = (
  pricePerStdUnit: number,
  portionsUnit: string,
  countUnitLabel?: string
): string => {
  if (portionsUnit === 'g') {
    return `${eur(pricePerStdUnit * 1000)}/kg`;
  }
  if (portionsUnit === 'ml') {
    return `${eur(pricePerStdUnit * 1000)}/L`;
  }
  return `${eur(pricePerStdUnit)}/${countUnitLabel ?? getPortionsUnitDisplay(portionsUnit)}`;
};

/**
 * Calculates nested cost price item information
 */
export const getNestedCostPriceInfo = (
  ingredients: CostPriceAnalysis['ingredients']
) => {
  const firstIngredient = ingredients[0] as any;
  const nestedCostPriceName = firstIngredient?.nestedCostPriceName;
  const nestedQuantityNeeded = firstIngredient?.nestedQuantityNeeded || 0;

  const totalCost = firstIngredient.currentPrice
    ? Math.abs(firstIngredient.currentPrice) * nestedQuantityNeeded
    : 0;

  return {
    name: nestedCostPriceName,
    quantityNeeded: nestedQuantityNeeded,
    totalCost,
  };
};
